import styled, { css } from "styled-components";
import { useNavigate } from "react-router-dom";
import useStudentListView from "features/teacher/hooks/student/useStudentListView";
import Refresh from "shared/components/Refresh";
import Modal from "shared/components/Modal";
import { hw, scrollbar } from "features/homework/homeworkUi";

interface StudentListViewProps {
    clickedItem: string;
    setClickedItem: React.Dispatch<React.SetStateAction<string>>;
}

const statusMeta = (status: string) => {
    switch (status) {
        case "suspended":
            return { label: "휴학중", color: "#D97706", bg: "#FFFBEB" };
        case "graduated":
            return { label: "퇴원", color: "#64748B", bg: "#F1F5F9" };
        default:
            return { label: "재학중", color: hw.success, bg: "#F0FDF4" };
    }
};

const StudentListView = ({ clickedItem, setClickedItem }: StudentListViewProps) => {
    const navigate = useNavigate();
    const {
        searchedLists,
        onRefreshClick,
        search,
        onChange,
        selectedStudents,
        onStudentSelect,
        isOpenStatusModal,
        setIsOpenStatusModal,
        newStatus,
        onStatusChange,
        onBulkStatusUpdate,
        setSelectedStudents,
        isLoading,
        lessonList,
        onLessonChange,
        selectedLessonId,
    } = useStudentListView("");

    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setSelectedStudents(searchedLists.map(student => student.uid));
        } else {
            setSelectedStudents([]);
        }
    };

    const isAllSelected = searchedLists.length > 0 && selectedStudents.length === searchedLists.length;

    return (
        <Container>
            <Header>
                <div>
                    <Title>학생 목록</Title>
                    <Sub>학급·이름으로 찾고, 선택해 상태를 바꿀 수 있습니다.</Sub>
                </div>
                <HeaderRight>
                    {selectedStudents.length > 0 && (
                        <StatusButton type="button" onClick={() => setIsOpenStatusModal(true)}>
                            상태 변경
                            <Count>{selectedStudents.length}</Count>
                        </StatusButton>
                    )}
                    <Refresh onRefreshClick={onRefreshClick} />
                </HeaderRight>
            </Header>

            <Filters>
                <FilterGroup>
                    <FilterLabel htmlFor="student-lesson">학급</FilterLabel>
                    <LessonSelect
                        id="student-lesson"
                        value={selectedLessonId}
                        onChange={(e) => onLessonChange(e.target.value)}
                    >
                        <option value="">전체</option>
                        {lessonList.map((lesson) => (
                            <option key={lesson.id} value={lesson.id}>
                                {lesson.name}
                            </option>
                        ))}
                    </LessonSelect>
                </FilterGroup>
                <FilterGroup>
                    <FilterLabel htmlFor="student-search">검색</FilterLabel>
                    <SearchInput
                        id="student-search"
                        placeholder="학생 이름"
                        value={search}
                        onChange={onChange}
                    />
                </FilterGroup>
            </Filters>

            <List>
                <ListHeader>
                    <Checkbox
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                        aria-label="전체 선택"
                    />
                    <HeaderText>이름</HeaderText>
                    <HeaderText $right>상태</HeaderText>
                </ListHeader>
                {searchedLists.length === 0 ? (
                    <Empty>
                        <EmptyTitle>{search ? "검색 결과가 없습니다" : "학생이 없습니다"}</EmptyTitle>
                        <EmptyText>
                            {search ? "다른 이름으로 다시 검색해 보세요." : "생성하기를 눌러 원생을 등록하세요."}
                        </EmptyText>
                    </Empty>
                ) : (
                    searchedLists.map((item) => {
                        const status = statusMeta(item.status === undefined ? "active" : item.status);
                        const selected = item.uid === clickedItem;
                        return (
                            <Item
                                key={item.uid}
                                $on={selected}
                                onClick={() => setClickedItem(item.uid)}
                            >
                                <Checkbox
                                    type="checkbox"
                                    checked={selectedStudents.includes(item.uid)}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                        e.stopPropagation();
                                        onStudentSelect(item.uid);
                                    }}
                                    aria-label={`${item.name} 선택`}
                                />
                                <StudentName>{item.name}</StudentName>
                                <StatusBadge $color={status.color} $bg={status.bg}>
                                    {status.label}
                                </StatusBadge>
                            </Item>
                        );
                    })
                )}
            </List>

            <Buttons>
                <GhostButton type="button" onClick={() => navigate("register")}>
                    계정 복구
                </GhostButton>
                {clickedItem ? (
                    <GhostButton type="button" onClick={() => navigate("register", { state: clickedItem })}>
                        수정하기
                    </GhostButton>
                ) : null}
                <CreateButton type="button" onClick={() => navigate("register")}>
                    생성하기
                </CreateButton>
            </Buttons>

            <Modal
                isOpen={isOpenStatusModal}
                title="학생 상태 변경"
                content={
                    <StatusModalContent>
                        <StatusSelect value={newStatus} onChange={onStatusChange}>
                            <option value="active">재학중</option>
                            <option value="suspended">휴학중</option>
                            <option value="graduated">퇴원</option>
                        </StatusSelect>
                        <SelectedCount>선택된 학생 {selectedStudents.length}명</SelectedCount>
                        {isLoading ? <LoadingText>상태 변경 중...</LoadingText> : null}
                    </StatusModalContent>
                }
                onClose={() => setIsOpenStatusModal(false)}
                onConfirm={onBulkStatusUpdate}
            />
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    min-width: 0;
    min-height: 0;
    height: 100%;
    padding: 20px 18px 16px;
    background: ${hw.surface};
    border: 1px solid ${hw.border};
    border-radius: 16px;
    box-shadow: ${hw.shadow};
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid ${hw.border};
    flex-shrink: 0;
`;

const HeaderRight = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
`;

const Title = styled.h2`
    margin: 0;
    color: ${hw.text};
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.02em;
`;

const Sub = styled.p`
    margin: 4px 0 0;
    font-size: 13px;
    color: ${hw.textMuted};
`;

const Filters = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    flex-shrink: 0;
`;

const FilterGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const FilterLabel = styled.label`
    color: ${hw.textMuted};
    font-size: 12px;
    font-weight: 600;
`;

const fieldCss = css`
    width: 100%;
    padding: 9px 12px;
    border: 1px solid ${hw.border};
    border-radius: 8px;
    background: ${hw.surface};
    color: ${hw.text};
    font-size: 13px;
    font-weight: 500;
    box-sizing: border-box;

    &:focus {
        outline: none;
        border-color: ${hw.brand};
        box-shadow: 0 0 0 3px rgba(223, 0, 0, 0.12);
    }
`;

const LessonSelect = styled.select`
    ${fieldCss}
    cursor: pointer;
`;

const SearchInput = styled.input`
    ${fieldCss}

    &::placeholder {
        color: ${hw.textSubtle};
    }
`;

const List = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    border-radius: 12px;
    border: 1px solid ${hw.border};
    background: ${hw.surface};
    overflow-y: auto;
    overflow-x: hidden;
    ${scrollbar}
`;

const ListHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    background: #FAFBFC;
    border-bottom: 1px solid ${hw.border};
    position: sticky;
    top: 0;
    z-index: 1;
`;

const HeaderText = styled.span<{ $right?: boolean }>`
    color: ${hw.textMuted};
    font-size: 12px;
    font-weight: 600;
    flex: ${props => props.$right ? "0 0 auto" : "1"};
`;

const Item = styled.div<{ $on: boolean }>`
    width: 100%;
    padding: 12px 14px;
    border-bottom: 1px solid ${hw.border};
    background: ${props => props.$on ? hw.brandSoft : hw.surface};
    box-shadow: ${props => props.$on ? `inset 3px 0 0 ${hw.brand}` : "none"};
    color: ${hw.text};
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 10px;
    box-sizing: border-box;

    &:hover {
        background: ${props => props.$on ? hw.brandSoft : hw.bg};
    }
`;

const Checkbox = styled.input`
    width: 16px;
    height: 16px;
    margin: 0;
    cursor: pointer;
    accent-color: ${hw.brand};
    flex-shrink: 0;
`;

const StudentName = styled.span`
    flex: 1;
    min-width: 0;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const StatusBadge = styled.span<{ $color: string; $bg: string }>`
    display: inline-flex;
    padding: 3px 8px;
    border-radius: 999px;
    background: ${props => props.$bg};
    color: ${props => props.$color};
    font-weight: 700;
    font-size: 11px;
    flex-shrink: 0;
`;

const Empty = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 36px 16px;
    text-align: center;
`;

const EmptyTitle = styled.div`
    font-size: 14px;
    font-weight: 700;
    color: ${hw.text};
`;

const EmptyText = styled.div`
    font-size: 13px;
    color: ${hw.textMuted};
    line-height: 1.5;
`;

const Buttons = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 12px;
    border-top: 1px solid ${hw.border};
    flex-shrink: 0;
`;

const GhostButton = styled.button`
    appearance: none;
    padding: 9px 14px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    border: 1px solid ${hw.border};
    background: ${hw.surface};
    color: ${hw.textMuted};

    &:hover {
        background: ${hw.bg};
        color: ${hw.text};
    }
`;

const CreateButton = styled.button`
    appearance: none;
    padding: 9px 16px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    border: none;
    background: ${hw.brand};
    color: white;

    &:hover {
        background: ${hw.brandDark};
    }
`;

const StatusButton = styled.button`
    appearance: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 10px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    border: 1px solid ${hw.brandMuted};
    background: ${hw.brandSoft};
    color: ${hw.brand};

    &:hover {
        background: ${hw.brandMuted};
    }
`;

const Count = styled.span`
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 999px;
    background: ${hw.brand};
    color: white;
    font-size: 11px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
`;

const StatusModalContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-items: center;
`;

const StatusSelect = styled.select`
    padding: 10px 12px;
    border: 1px solid ${hw.border};
    border-radius: 8px;
    font-size: 14px;
    color: ${hw.text};
    background: white;
    cursor: pointer;
    width: 200px;

    &:focus {
        outline: none;
        border-color: ${hw.brand};
        box-shadow: 0 0 0 3px rgba(223, 0, 0, 0.12);
    }
`;

const SelectedCount = styled.div`
    color: ${hw.textMuted};
    font-size: 13px;
`;

const LoadingText = styled.div`
    color: ${hw.textMuted};
    font-size: 13px;
`;

export default StudentListView;
